/**
 * GovTasksPage — Cross-entity task inbox.
 * Aggregates open checklist items from all live solicitations and construction
 * jobs into one view. Filter by assignee/entity, click to jump to the source.
 * Route: /gov/tasks
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { S, GOV_COLORS, deadlineColor, daysUntil } from './govShared';

interface GovTask {
  id: string;
  _type: 'solicitation_task' | 'construction_task';
  task_key: string;
  task_label: string;
  entity_type: 'gov_solicitation' | 'gov_construction_job';
  entity_id: string;
  entity_label: string;
  agency_name?: string;
  pipeline: 'sources_sought' | 'active' | 'construction';
  stage: string;
  due_at?: string | null;
  assigned_to?: string | null;
  assignee?: { id: string; name: string; email: string } | null;
}

interface User { id: string; name: string; email: string; }

const PIPELINE_META: Record<string, { label: string; icon: string; color: string }> = {
  sources_sought: { label: 'Sources Sought', icon: '🎯', color: '#8B5CF6' },
  active:         { label: 'Active',         icon: '📑', color: '#4A7EB5' },
  construction:   { label: 'Construction',   icon: '🏗️', color: '#22C55E' },
};

export default function GovTasksPage() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<GovTask[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [assignee, setAssignee] = useState<string>('all');
  const [entity, setEntity] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [groupBy, setGroupBy] = useState<'due' | 'assignee' | 'entity'>('due');

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (assignee !== 'all') params.set('assignee', assignee);
      if (entity !== 'all') params.set('entity', entity);
      const { data } = await api.get(`/gov/tasks?${params.toString()}`);
      setTasks(data?.tasks || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [assignee, entity]);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await api.get('/users');
      setUsers(Array.isArray(data) ? data : data?.users || []);
    } catch { /* users endpoint optional */ }
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);
  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tasks;
    return tasks.filter(t =>
      t.task_label.toLowerCase().includes(q) ||
      t.entity_label.toLowerCase().includes(q) ||
      (t.agency_name || '').toLowerCase().includes(q));
  }, [tasks, search]);

  const grouped = useMemo(() => {
    const buckets: Array<{ label: string; items: GovTask[]; color?: string }> = [];
    if (groupBy === 'due') {
      const overdue: GovTask[] = [];
      const in7: GovTask[] = [];
      const in30: GovTask[] = [];
      const later: GovTask[] = [];
      const noDate: GovTask[] = [];
      filtered.forEach(t => {
        const d = daysUntil(t.due_at || undefined);
        if (d == null) noDate.push(t);
        else if (d < 0) overdue.push(t);
        else if (d <= 7) in7.push(t);
        else if (d <= 30) in30.push(t);
        else later.push(t);
      });
      if (overdue.length) buckets.push({ label: `🚨 Overdue (${overdue.length})`, items: overdue, color: GOV_COLORS.danger });
      if (in7.length) buckets.push({ label: `⏰ Next 7 days (${in7.length})`, items: in7, color: GOV_COLORS.warning });
      if (in30.length) buckets.push({ label: `📅 Next 30 days (${in30.length})`, items: in30, color: GOV_COLORS.caution });
      if (later.length) buckets.push({ label: `📆 Later (${later.length})`, items: later, color: GOV_COLORS.accent });
      if (noDate.length) buckets.push({ label: `❓ No due date (${noDate.length})`, items: noDate, color: GOV_COLORS.textDim as any });
    } else if (groupBy === 'assignee') {
      const byUser: Record<string, GovTask[]> = {};
      filtered.forEach(t => {
        const key = t.assignee?.name || 'Unassigned';
        (byUser[key] ||= []).push(t);
      });
      Object.entries(byUser).sort(([a],[b]) => a === 'Unassigned' ? 1 : b === 'Unassigned' ? -1 : a.localeCompare(b))
        .forEach(([label, items]) => buckets.push({ label: `${label === 'Unassigned' ? '👻' : '👤'} ${label} (${items.length})`, items }));
    } else {
      const byEntity: Record<string, GovTask[]> = {};
      filtered.forEach(t => { (byEntity[t.entity_label] ||= []).push(t); });
      Object.entries(byEntity).forEach(([label, items]) => buckets.push({ label: `📁 ${label} (${items.length})`, items }));
    }
    return buckets;
  }, [filtered, groupBy]);

  const openTask = (t: GovTask) => {
    if (t.entity_type === 'gov_solicitation') navigate(`/gov/solicitations/${t.entity_id}`);
    else navigate(`/gov/construction/${t.entity_id}`);
  };

  const formatDue = (iso?: string | null): { label: string; color: string } | null => {
    if (!iso) return null;
    const d = daysUntil(iso);
    if (d == null) return null;
    const dateLbl = new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    if (d < 0) return { label: `${Math.abs(d)}d late · ${dateLbl}`, color: GOV_COLORS.danger };
    if (d === 0) return { label: `Today · ${dateLbl}`, color: GOV_COLORS.danger };
    return { label: `${d}d · ${dateLbl}`, color: deadlineColor(d) };
  };

  return (
    <div style={S.container}>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>📋 Tasks</h1>
          <p style={S.subtitle}>{filtered.length} open · aggregated from all live solicitations & construction jobs</p>
        </div>
      </div>

      <div style={S.toolbar}>
        <input type="text" placeholder="🔍 Search tasks…" value={search} onChange={e => setSearch(e.target.value)} style={S.input} />
        <select value={assignee} onChange={e => setAssignee(e.target.value)} style={S.select}>
          <option value="all">👥 All assignees</option>
          <option value="unassigned">👻 Unassigned</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <select value={entity} onChange={e => setEntity(e.target.value)} style={S.select}>
          <option value="all">📂 All types</option>
          <option value="solicitation">📑 Solicitations</option>
          <option value="construction">🏗️ Construction</option>
        </select>
        <select value={groupBy} onChange={e => setGroupBy(e.target.value as any)} style={S.select}>
          <option value="due">Group: Due date</option>
          <option value="assignee">Group: Assignee</option>
          <option value="entity">Group: Solicitation / Job</option>
        </select>
      </div>

      {loading ? (
        <div style={S.loading}>Loading tasks…</div>
      ) : grouped.length === 0 ? (
        <div style={S.empty}>🎉 No open tasks. Everything's checked off.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {grouped.map(bucket => (
            <div key={bucket.label}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <div style={{ width: 4, height: 18, background: bucket.color || GOV_COLORS.accent, borderRadius: 2 }} />
                <h3 style={{ ...S.panelTitle, marginBottom: 0 }}>{bucket.label}</h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {bucket.items.map(t => {
                  const pipeMeta = PIPELINE_META[t.pipeline];
                  const due = formatDue(t.due_at);
                  return (
                    <div key={t.id} onClick={() => openTask(t)} style={S.listRow}
                      onMouseEnter={e => (e.currentTarget.style.background = GOV_COLORS.cardHi)}
                      onMouseLeave={e => (e.currentTarget.style.background = GOV_COLORS.card)}>
                      <div style={{ ...S.chip, background: pipeMeta.color + '22', color: pipeMeta.color, minWidth: 34, justifyContent: 'center' }}>
                        {pipeMeta.icon}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: '#fff', fontSize: 14, fontWeight: 600, marginBottom: 2 }}>{t.task_label}</div>
                        <div style={{ color: GOV_COLORS.textMuted, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {t.entity_label}
                          {t.agency_name && <span style={{ color: GOV_COLORS.textDim, marginLeft: 8 }}>· {t.agency_name}</span>}
                        </div>
                      </div>
                      {t.assignee ? (
                        <div style={{ ...S.chip, background: '#3B82F622', color: '#3B82F6' }}>
                          👤 {t.assignee.name}
                        </div>
                      ) : (
                        <div style={{ ...S.chip, background: '#64748B22', color: '#94a3b8' }}>👻 Unassigned</div>
                      )}
                      {due && (
                        <div style={{ ...S.chip, background: due.color + '22', color: due.color, minWidth: 100, justifyContent: 'center' }}>
                          {due.label}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
